import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Customer, DiscountDocumentType, Lead } from "../api/models";
import {
  ConfigurationBuildStep,
  ConfigurationStep,
  LeadConfiguration,
  LeadConfigurationId,
  LeadConfigurationType,
} from "../api/models/configuration";
import { State } from "../store/interfaces";
import {
  ConfigurationActions,
  ConfigurationLeadData,
  configurationLoadedAction,
  configurationLoadErrorAction,
  openConfigurationModalAction,
  requestConfigurationAction,
  resetConfigurationData,
  setConfigurationCurrentStepAction,
  setConfigurationSelectedAction,
  setConfigurationLeadAction,
  setConfigurationCurrentBuildStepAction,
} from "../store/reducers/configuration";
import { configurationSavedAction } from "../store";
import { CoreApiClient } from "..";
import { LeadRequirement } from "../api/models/requirement";

export const useConfiguration = (api: CoreApiClient) => {
  const configurationState = useSelector((state: State) => state.configuration);
  const appSettings = useSelector((state: State) => state.appSettings);
  const [isRemovingConfiguration, setIsRemovingConfiguration] = useState(false);
  const [isSavingConfiguration, setIsSavingConfiguration] = useState(false);
  const dispatch = useDispatch();

  const _load = async (
    data: ConfigurationLeadData,
    options?: { currentStep?: ConfigurationStep }
  ) => {
    dispatch(requestConfigurationAction(data));

    try {
      let configuration: LeadRequirement | undefined;
      const documentTypes: DiscountDocumentType[] = [];
      if (data.leadId && data.requirementId) {
        const [response] = await Promise.all([
          api.requirement.getRequirement(data.requirementId),
        ]);
        configuration = response;

        dispatch(configurationSavedAction(configuration));
      }
      dispatch(
        configurationLoadedAction({
          ...data,
          configuration,
          currentStep: configuration ? "done" : "lead",
          documentTypes,
          packagePriceCalculationSettings:
            appSettings.data?.packagePriceCalculationSettings,
        })
      );
      if (data.lead) {
        dispatch(setConfigurationLeadAction(data.lead));
      }
      if (options?.currentStep) {
        dispatch(setCurrentStep(options.currentStep));
      }
    } catch (e) {
      dispatch(
        configurationLoadErrorAction({
          ...data,
          error: e,
        })
      );
    }
  };

  const load = (data: ConfigurationLeadData) => {
    if (
      configurationState.leadId &&
      configurationState.requirementId &&
      data.requirementId === configurationState.requirementId &&
      data.leadId === configurationState.leadId
    ) {
      return;
    }
    _load(data);
  };

  const reset = () => {
    dispatch(resetConfigurationData());
  };

  const setCurrentStep = (currentStep?: ConfigurationStep) => {
    dispatch(setConfigurationCurrentStepAction(currentStep));
  };

  const setCurrentBuildStep = (currentBuildStep?: ConfigurationBuildStep) => {
    dispatch(setConfigurationCurrentBuildStepAction(currentBuildStep));
  };

  const openModal = (data: {
    lead?: Lead;
    requirementId?: string;
    customer?: Customer;
    buildMode?: boolean;
    fromLead?: boolean;
  }) => {
    load({
      lead: data.lead,
      leadId: data.lead?._id,
      requirementId: data.requirementId,
    });
    dispatch(
      openConfigurationModalAction({
        open: true,
        buildMode: data.buildMode,
        fromLead: data.fromLead,
      })
    );
    if (data.buildMode) {
      setCurrentBuildStep(data.requirementId ? "build" : "vehicle");
    }
  };

  const closeModal = () => {
    dispatch(openConfigurationModalAction({ open: false }));
    reset();
  };

  const reload = (options?: {
    currentStep?: ConfigurationStep;
    requirementId?: string;
  }) => {
    if (
      !configurationState.leadId ||
      !(options?.requirementId || configurationState.requirementId)
    ) {
      return;
    }
    _load(
      {
        lead: configurationState.lead,
        leadId: configurationState.leadId,
        requirementId:
          options?.requirementId || configurationState.requirementId,
      },
      options
    );
  };

  const removeConfiguration = async (
    data: LeadConfigurationId,
    onSunccess?: () => void,
    onError?: (e: any) => void
  ) => {
    setIsRemovingConfiguration(true);
    try {
      await api.configuration.deleteConfiguration(data.leadId, data.configId);
      onSunccess?.();
    } catch (e) {
      onError?.(e);
    }
    setIsRemovingConfiguration(false);
  };

  const setSelectedAction = (action?: ConfigurationActions) => {
    dispatch(setConfigurationSelectedAction(action));
  };

  const setConfigurationLead = (lead: Lead) => {
    dispatch(setConfigurationLeadAction(lead));
  };

  return {
    ...configurationState,
    load,
    reload,
    reset,
    openModal,
    closeModal,
    removeConfiguration,
    isRemovingConfiguration,
    setSelectedAction,
    isSavingConfiguration,
    anyUpdateHappening: configurationState.loading || isSavingConfiguration,
    setCurrentStep,
    setConfigurationLead,
    setCurrentBuildStep,
  };
};
